/**
 * AdminEditorialCouncil, Módulo 2 (Iter 34).
 *
 * Textarea para colar o texto → botão "Revisar" → chama
 * POST /api/admin/editorial/review e exibe:
 *   - diff lado a lado (original vs revisado)
 *   - lista de mudanças
 *   - alertas teológicos em destaque (vermelho)
 *   - botão "Copiar texto revisado"
 *
 * Não intercepta publicações. Composição pura no /admin.
 */
import React, { useState } from "react";
import { PenLine, Wand2, Copy, AlertTriangle, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const CONTENT_TYPES = [
  { v: "generic", label: "Genérico" },
  { v: "devocional", label: "Devocional" },
  { v: "sermao", label: "Sermão" },
  { v: "plano", label: "Plano de leitura" },
  { v: "oracao", label: "Oração" },
  { v: "frase", label: "Frase do dia" },
  { v: "email", label: "E-mail / newsletter" },
];

export default function AdminEditorialCouncil() {
  const [text, setText] = useState("");
  const [contentType, setContentType] = useState("generic");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [copyOk, setCopyOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (t.length < 10) {
      setErr("Cole um texto com pelo menos 10 caracteres.");
      return;
    }
    setLoading(true);
    setErr(null);
    setResult(null);
    try {
      const { data } = await api.post("/admin/editorial/review", {
        text: t,
        content_type: contentType,
      });
      setResult(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Falha ao revisar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const copyRevised = async () => {
    if (!result?.texto_revisado) return;
    try {
      await navigator.clipboard.writeText(result.texto_revisado);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch {
      setErr("Não foi possível copiar. Selecione manualmente.");
    }
  };

  return (
    <section
      data-testid="admin-editorial-council-section"
      className="bg-white rounded-3xl shadow-sm border border-[#0B1E36]/8 p-6 md:p-8 mt-8"
    >
      <header className="flex items-center gap-3 mb-5">
        <PenLine className="w-5 h-5 text-[#C5A059]" />
        <div>
          <p className="text-[0.7rem] uppercase tracking-[0.18em] text-[#0B1E36]/55">
            Módulo editorial
          </p>
          <h2 className="font-serif-display text-2xl text-[#0B1E36]">
            Conselho editorial
          </h2>
          <p className="text-xs text-[#0B1E36]/60 mt-1">
            Cole um texto para revisão antes de publicar. Corrige linguagem,
            padrão CM, e alerta erros teológicos.
          </p>
        </div>
      </header>

      {err && (
        <div
          data-testid="editorial-error"
          className="mb-4 px-3 py-2 rounded bg-rose-50 border border-rose-200 text-rose-700 text-sm inline-flex items-center gap-2"
        >
          <AlertTriangle className="w-4 h-4" />
          {err}
        </div>
      )}

      <form
        onSubmit={submit}
        data-testid="editorial-form"
        className="grid md:grid-cols-12 gap-4 mb-6"
      >
        <div className="md:col-span-9">
          <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
            Texto para revisar
          </label>
          <textarea
            data-testid="editorial-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            maxLength={8000}
            placeholder="Cole aqui o rascunho do devocional, sermão, e-mail ou frase..."
            className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm font-sans-body"
          />
          <p className="text-xs text-[#0B1E36]/45 mt-1">{text.length}/8000</p>
        </div>

        <div className="md:col-span-3 flex flex-col gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#0B1E36]/70 mb-1">
              Tipo de conteúdo
            </label>
            <select
              data-testid="editorial-type"
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className="w-full px-3 py-2 rounded border border-[#0B1E36]/15 text-sm bg-white"
            >
              {CONTENT_TYPES.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="editorial-submit"
            className="mt-auto px-4 py-2.5 rounded bg-[#C5A059] hover:bg-[#a88848] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Revisando...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" /> Revisar texto
              </>
            )}
          </button>
        </div>
      </form>

      {result && (
        <div data-testid="editorial-result">
          {/* Status: aprovado ou alerta */}
          {result.aprovado ? (
            <div
              data-testid="editorial-status-ok"
              className="mb-4 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm inline-flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Texto aprovado pelo Conselho Editorial.
            </div>
          ) : (
            <div
              data-testid="editorial-status-blocked"
              className="mb-4 px-4 py-3 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-sm inline-flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4" />
              Requer atenção antes de publicar. Veja as observações abaixo.
            </div>
          )}

          {/* Alertas teológicos em destaque */}
          {result.alertas_teologicos?.length > 0 && (
            <div
              data-testid="editorial-alerts"
              className="mb-5 rounded-xl border-2 border-rose-300 bg-rose-50 p-4"
            >
              <div className="flex items-center gap-2 mb-2 text-rose-800">
                <AlertTriangle className="w-4 h-4" />
                <h3 className="text-sm font-semibold">Alertas teológicos</h3>
              </div>
              <ul className="list-disc list-inside space-y-1 text-sm text-rose-800">
                {result.alertas_teologicos.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Diff lado a lado */}
          <div className="grid md:grid-cols-2 gap-4 mb-5">
            <div>
              <p className="text-[0.7rem] uppercase tracking-wider text-[#0B1E36]/55 mb-2">
                Original
              </p>
              <pre
                data-testid="editorial-original"
                className="whitespace-pre-wrap text-sm p-3 rounded-lg bg-[#FAF8F4] border border-[#0B1E36]/10 text-[#0B1E36]/75 leading-relaxed font-sans-body"
              >
                {text}
              </pre>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[0.7rem] uppercase tracking-wider text-[#C5A059]">
                  Revisado
                </p>
                <button
                  type="button"
                  onClick={copyRevised}
                  data-testid="editorial-copy"
                  className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-[#0B1E36] hover:bg-[#122a4c] text-white"
                >
                  <Copy className="w-3 h-3" />
                  {copyOk ? "Copiado!" : "Copiar"}
                </button>
              </div>
              <pre
                data-testid="editorial-revised"
                className="whitespace-pre-wrap text-sm p-3 rounded-lg bg-[#C5A059]/8 border border-[#C5A059]/40 text-[#0B1E36] leading-relaxed font-sans-body"
              >
                {result.texto_revisado}
              </pre>
            </div>
          </div>

          {/* Lista de mudanças */}
          {result.mudancas?.length > 0 && (
            <div data-testid="editorial-changes">
              <p className="text-[0.7rem] uppercase tracking-wider text-[#0B1E36]/55 mb-2">
                Mudanças realizadas ({result.mudancas.length})
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-[#0B1E36]/80">
                {result.mudancas.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
